'use client';

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Box, Fade, Button, Typography, Paper, CircularProgress } from '@mui/material';
import { useEstimator } from '../../context/EstimatorContext';
import useSaveQuantities from '../../hooks/useSaveQuantities';
import { AgGridReact } from 'ag-grid-react';
import {
  ModuleRegistry,
  ColumnAutoSizeModule,
  RowStyleModule,
  CellStyleModule,
  TooltipModule,
  RowSelectionModule,
  NumberEditorModule,
  GridStateModule,
  ColumnApiModule,
  RowApiModule,
  CellApiModule,
  ScrollApiModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
} from 'ag-grid-community';
import { RowNumbersModule, CellSelectionModule, ClipboardModule } from 'ag-grid-enterprise';

ModuleRegistry.registerModules([
  ColumnAutoSizeModule,
  RowStyleModule,
  CellStyleModule,
  TooltipModule,
  RowSelectionModule,
  NumberEditorModule,
  GridStateModule,
  ColumnApiModule,
  RowApiModule,
  CellApiModule,
  ScrollApiModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  RowNumbersModule,
  CellSelectionModule,
  ClipboardModule,
]);

const Quantities = ({ onComplete }) => {
  const { formData, setFormData } = useEstimator();
  const [loading, setLoading] = useState(false);
  const saveQuantities = useSaveQuantities();

  const initialGridData = useMemo(
    () => [
      { no: 1, category: 'General', type: 'Diameter', units: 'Inch', dataSource: 'PM' },
      { no: 2, category: 'General', type: 'Product', units: 'Each', dataSource: 'PM' },
      { no: 3, category: 'General', type: 'Route Length 2D ', quantity: 0.0, units: 'Feet', dataSource: 'GIS' },
      { no: 4, category: 'General', type: 'Route Length 2D', units: 'Miles', dataSource: 'GIS' },
      {
        no: 5,
        category: 'General',
        type: 'Sloper Adj %',
        quantity: 0.0,
        units: '%',
        dataSource: 'Calc',
        notes: '0.1% in areas of low or moderate slope. For high slope calc by 3D profile',
      },
      { no: 6, category: 'General', type: 'Route Length 3D', units: 'Feet', dataSource: 'Calc' },
      { no: 7, category: 'General', type: 'Route Length 3D', quantity: 0.0, units: 'Miles', dataSource: 'Calc' },
      {
        no: 8,
        category: 'General',
        type: 'Spreads',
        units: 'Each',
        notes: '100 miles in open areas, 50-100 miles in urban, rocky, or wet areas',
        dataSource: 'PM',
      },
      {
        no: 9,
        category: 'General',
        type: 'MAOP',
        units: 'psi',
        dataSource: 'PM',
      },
      {
        no: 10,
        category: 'Crossings',
        type: 'Construction ROW Width',
        units: 'Feet',
        notes: "8-12': 75', 16-24': 100', 30-36': 115', 42': 125', 48': 150'",
        dataSource: 'PM',
      },
      { no: 11, category: 'Crossings', type: 'Miles of Access Roads', units: 'Miles', dataSource: 'GIS' },
      { no: 12, category: 'Crossings', type: 'Tract Count', units: 'Each', dataSource: 'TNRIS Parcels, Transect' },
      {
        no: 13,
        category: 'Crossings',
        type: 'Number of Bores (Class 1)',
        units: 'Each',
        notes: 'Public roads in class I segments',
        dataSource: 'GIS',
      },
      {
        no: 14,
        category: 'Crossings',
        type: 'Length of Bores (Class 1)',
        units: 'Feet',
        notes: 'Average 160’ each',
        dataSource: 'GIS',
      },
      {
        no: 15,
        category: 'Crossings',
        type: 'Number of Bores (Class 2)',
        units: 'Each',
        notes: 'Railroads, and public roads in class II & III segments',
        dataSource: 'GIS',
      },
      {
        no: 16,
        category: 'Crossings',
        type: 'Length of Bores (Class 2)',
        units: 'Feet',
        notes: 'Average 200’ each',
        dataSource: 'GIS',
      },
      {
        no: 17,
        category: 'Crossings',
        type: 'Number of Bores (Class 3)',
        units: 'Each',
        notes: 'Railroads, and public roads in class II & III segments',
        dataSource: 'GIS',
      },
      {
        no: 18,
        category: 'Crossings',
        type: 'Length of Bores (Class 3)',
        units: 'Feet',
        notes: 'Average 200’ each',
        dataSource: 'GIS',
      },
      { no: 19, category: 'Crossings', type: 'Number of HDDs and Direct Pipe', units: 'Each', dataSource: 'GIS' },
      { no: 20, category: 'Crossings', type: 'Length of HDDs', units: 'Feet', dataSource: 'GIS' },
      { no: 21, category: 'Crossings', type: 'Direct Pipe', units: 'Feet', dataSource: 'GIS' },
      { no: 22, category: 'Crossings', type: 'Open Cut Heavy Haul Private Roads', units: 'Each', dataSource: 'GIS' },
      { no: 23, category: 'Crossings', type: 'Open Cut Dirt Roads', units: 'Each', dataSource: 'GIS' },
      { no: 24, category: 'Crossings', type: 'Length of Class 2 Segment', units: 'Feet', dataSource: 'GIS' },
      { no: 25, category: 'Crossings', type: 'Length of Class 3 Segment', units: 'Feet', dataSource: 'GIS' },
      { no: 26, category: 'Crossings', type: 'Length of Class 4 Segment', units: 'Feet', dataSource: 'GIS' },
      { no: 27, category: 'Crossings', type: 'Foreign Line Crossings', units: 'Each', dataSource: 'Rextag or TXRRC' },
      { no: 28, category: 'Crossings', type: 'Collocation', units: 'Miles', dataSource: 'Rextag' },
      {
        no: 29,
        category: 'Crossings',
        type: 'Rock Ditch',
        units: 'Feet',
        dataSource: 'Esri Depth to Surface Bedrock or Geology Map',
      },
      { no: 30, category: 'Crossings', type: 'Heavy Timber Clearing', units: 'Feet', dataSource: 'GIS' },
      {
        no: 31,
        category: 'Crossings',
        type: 'Open Cut Wetlands & Waterbodies',
        units: 'Feet',
        dataSource: 'National Wetlands Inventory',
      },
      {
        no: 32,
        category: 'Crossings',
        type: 'Flood Plain or Hydric Soil',
        units: 'Feet',
        dataSource: 'FEMA or SSURGO',
      },
      {
        no: 33,
        category: 'Materials',
        type: 'Mats',
        units: 'Each',
        notes:
          '10 per HDD + 4 per Bore + 6 per FPL + Wetland/Waterbody Length/4 + (50% of Flood Plain length - Wetland & Waterbodies)/4',
        dataSource: 'Calc',
      },
      {
        no: 34,
        category: 'Materials',
        type: 'Segmentable Elbows',
        units: 'Each',
        notes: '2 per mile',
        dataSource: 'PM',
      },
      { no: 35, category: 'Materials', type: 'Mainline Valves', units: 'Each', dataSource: 'PM' },
      { no: 36, category: 'Materials', type: 'Future Tap', units: 'Each', dataSource: 'PM' },
      { no: 37, category: 'Materials', type: 'Pig Trap Connection', units: 'Each', dataSource: 'PM' },
      {
        no: 38,
        category: 'Materials',
        type: 'Pig Trap Barrels',
        units: 'Each',
        notes: 'NG typically no barrels, NGL with barrels',
        dataSource: 'PM',
      },
      {
        no: 39,
        category: 'Materials',
        type: 'CP Test Stations',
        quantity: 0.0,
        units: 'Each',
        notes: '1/mile+1/FPL+2/Bore&HDD',
        dataSource: 'Calc',
      },
      {
        no: 40,
        category: 'Materials',
        type: 'Anode & Rectifier',
        quantity: 0.0,
        units: 'Each',
        notes: '1 per 100 miles, rounded up',
        dataSource: 'Calc',
      },
      {
        no: 41,
        category: 'Materials',
        type: 'AC Mitigation',
        units: 'Feet',
        notes: 'Distance collocated with power transmission',
        dataSource: 'Rextag Power Transmission',
      },
      {
        no: 42,
        category: 'Materials',
        type: 'Hot Tap',
        units: 'Each',
        notes: 'Count of hot taps per project',
        dataSource: 'PM',
      },
    ],
    []
  );

  const [gridData, setGridData] = useState(() => {
    const source = formData?.quantities?.length ? formData.quantities : initialGridData;

    return source.map((row, index) => ({
      ...row,
      no: row.no ?? row.rowNumber ?? index + 1,
      rowNumber: row.rowNumber ?? row.no ?? index + 1,
    }));
  });

  const noToIndexMap = useMemo(() => {
    const map = {};
    gridData.forEach((row, index) => {
      map[row.no] = index;
    });
    return map;
  }, [gridData]);

  useEffect(() => {
    if (!gridData?.length || !Object.keys(noToIndexMap).length) return;

    const cloned = gridData.map((row) => ({ ...row }));
    recalcAll(cloned);
    setGridData(cloned);
  }, []);

  // Helper: Recompute any fields that depend on no 3 or no 5 (Route Lengths)
  const computeRouteLengthFields = (arr, indexMap) => {
    const index3 = indexMap[3];
    const index5 = indexMap[5];
    const index4 = indexMap[4];
    const index6 = indexMap[6];
    const index7 = indexMap[7];
    const index39 = indexMap[39]; // CP Test Stations
    const index40 = indexMap[40]; // Anode & Rectifier

    const lengthFeet = Number(arr[index3]?.quantity) || 0;
    const slopeAdjPercent = Number(arr[index5]?.quantity) || 0;
    const slopeMultiplier = 1 + slopeAdjPercent / 100;

    // Route Length 2D → Miles (no 4)
    if (index4 !== undefined) {
      const routeMilesRaw = lengthFeet / 5280;
      arr[index4] = {
        ...arr[index4],
        quantity: parseFloat(routeMilesRaw.toFixed(2)),
      };
    }

    // Route Length 3D (Feet) (no 6)
    if (index6 !== undefined) {
      const route3DFeet = lengthFeet * slopeMultiplier;
      arr[index6] = {
        ...arr[index6],
        quantity: parseFloat(route3DFeet.toFixed(2)),
      };
    }

    // Route Length 3D (Miles) (no 7)
    if (index7 !== undefined) {
      const route3DFeet = lengthFeet * slopeMultiplier;
      const route3DMiles = route3DFeet / 5280;
      arr[index7] = {
        ...arr[index7],
        quantity: parseFloat(route3DMiles.toFixed(2)),
      };
    }

    // If there is any lengthFeet, set CP Test Stations (no 39) and Anode (no 40) to zero
    if (index39 !== undefined) {
      arr[index39] = {
        ...arr[index39],
        // leave quantity as-is if user typed it; stats will be updated in a separate pass
        stats: lengthFeet > 0 ? `${Math.round(1 / (lengthFeet / 5280))} per mi` : '',
      };
    }
    if (index40 !== undefined) {
      arr[index40] = {
        ...arr[index40],
        stats: '', // always blank until something changes
      };
    }
  };

  // Helper: Recompute “Miles of Access Roads” stats (no 11) whenever no 3 or no 11 changes
  const computeAccessRoadStats = (arr, indexMap) => {
    const index3 = indexMap[3];
    const index11 = indexMap[11];
    if (index3 === undefined || index11 === undefined) return;

    const lengthFeet = Number(arr[index3]?.quantity) || 0;
    const miles2D = lengthFeet / 5280;
    const accessRoadMiles = Number(arr[index11]?.quantity) || 0;

    arr[index11] = {
      ...arr[index11],
      stats: miles2D > 0 ? `${(accessRoadMiles / miles2D).toFixed(1)} mi per route mi` : '',
    };
  };

  // Helper: Recompute “Tract Count” stats (no 12) → tracts per route mi
  const computeTractStats = (arr, indexMap) => {
    const index3 = indexMap[3];
    const index12 = indexMap[12];
    if (index3 === undefined || index12 === undefined) return;

    const lengthFeet = Number(arr[index3]?.quantity) || 0;
    const miles2D = lengthFeet / 5280;
    const tractCount = Number(arr[index12]?.quantity) || 0;

    arr[index12] = {
      ...arr[index12],
      stats: miles2D > 0 ? `${(tractCount / miles2D).toFixed(1)} mi per route mi` : '',
    };
  };

  // Helper: Recompute “Bores (Class 1)” stats (no 13) → count per route mi
  const computeBoreClass1Stats = (arr, indexMap) => {
    const index3 = indexMap[3];
    const index13 = indexMap[13];
    if (index3 === undefined || index13 === undefined) return;

    const lengthFeet = Number(arr[index3]?.quantity) || 0;
    const miles2D = lengthFeet / 5280;
    const bore1Count = Number(arr[index13]?.quantity) || 0;

    arr[index13] = {
      ...arr[index13],
      stats: miles2D > 0 ? `${(bore1Count / miles2D).toFixed(1)} per mi` : '',
    };
  };

  // Helper: “Length of Bores (Class 1)” stats (no 14) → average each
  const computeBoreClass1LengthStats = (arr, indexMap) => {
    const index13 = indexMap[13];
    const index14 = indexMap[14];
    if (index13 === undefined || index14 === undefined) return;

    const boreCount = Number(arr[index13]?.quantity) || 0;
    const totalLength = Number(arr[index14]?.quantity) || 0;

    arr[index14] = {
      ...arr[index14],
      stats: boreCount > 0 ? `Avg. ${(totalLength / boreCount).toFixed(1)} each` : '',
    };
  };

  // Helper: “Bores (Class 2)” stats (no 15) → count per route mi
  const computeBoreClass2Stats = (arr, indexMap) => {
    const index3 = indexMap[3];
    const index15 = indexMap[15];
    if (index3 === undefined || index15 === undefined) return;

    const lengthFeet = Number(arr[index3]?.quantity) || 0;
    const miles2D = lengthFeet / 5280;
    const bore2Count = Number(arr[index15]?.quantity) || 0;

    arr[index15] = {
      ...arr[index15],
      stats: miles2D > 0 ? `${(bore2Count / miles2D).toFixed(1)} per mi` : '',
    };
  };

  // Helper: “Length of Bores (Class 2)” stats (no 16) → average each
  const computeBoreClass2LengthStats = (arr, indexMap) => {
    const index15 = indexMap[15];
    const index16 = indexMap[16];
    if (index15 === undefined || index16 === undefined) return;

    const count2 = Number(arr[index15]?.quantity) || 0;
    const length2 = Number(arr[index16]?.quantity) || 0;

    arr[index16] = {
      ...arr[index16],
      stats: count2 > 0 ? `Avg. ${(length2 / count2).toFixed(1)} each` : '',
    };
  };

  // Helper: “Bores (Class 3)” stats (no 17) → count per route mi
  const computeBoreClass3Stats = (arr, indexMap) => {
    const index3 = indexMap[3];
    const index17 = indexMap[17];
    if (index3 === undefined || index17 === undefined) return;

    const lengthFeet = Number(arr[index3]?.quantity) || 0;
    const miles2D = lengthFeet / 5280;
    const bore3Count = Number(arr[index17]?.quantity) || 0;

    arr[index17] = {
      ...arr[index17],
      stats: miles2D > 0 ? `${Math.round(bore3Count / miles2D)} per mi` : '',
    };
  };

  // Helper: “Length of Bores (Class 3)” stats (no 18) → average each
  const computeBoreClass3LengthStats = (arr, indexMap) => {
    const index17 = indexMap[17];
    const index18 = indexMap[18];
    if (index17 === undefined || index18 === undefined) return;

    const count3 = Number(arr[index17]?.quantity) || 0;
    const length3 = Number(arr[index18]?.quantity) || 0;

    arr[index18] = {
      ...arr[index18],
      stats: count3 > 0 ? `Avg. ${(length3 / count3).toFixed(1)} each` : '',
    };
  };

  // Helper: “HDDs and Direct Pipe” stats (no 19) → count per route mi
  const computeHddStats = (arr, indexMap) => {
    const index3 = indexMap[3];
    const index19 = indexMap[19];
    if (index3 === undefined || index19 === undefined) return;

    const lengthFeet = Number(arr[index3]?.quantity) || 0;
    const miles2D = lengthFeet / 5280;
    const hddCount = Number(arr[index19]?.quantity) || 0;

    arr[index19] = {
      ...arr[index19],
      stats: miles2D > 0 ? `${(hddCount / miles2D).toFixed(1)} per mi` : '',
    };
  };

  // Helper: “Length of HDDs” stats (no 20) → average each
  const computeHddLengthStats = (arr, indexMap) => {
    const index19 = indexMap[19];
    const index20 = indexMap[20];
    if (index19 === undefined || index20 === undefined) return;

    const countHdd = Number(arr[index19]?.quantity) || 0;
    const lengthHdd = Number(arr[index20]?.quantity) || 0;

    arr[index20] = {
      ...arr[index20],
      stats: `Avg. ${Math.round(lengthHdd / countHdd)} each`,
    };
  };

  // Helper: “Open Cut Heavy Haul Private Roads” stats (no 22) → ft per route mi
  const computeOpenCutHAStats = (arr, indexMap) => {
    const index3 = indexMap[3];
    const index22 = indexMap[22];
    if (index3 === undefined || index22 === undefined) return;

    const lengthFeet = Number(arr[index3]?.quantity) || 0;
    const miles2D = lengthFeet / 5280;
    const val22 = Number(arr[index22]?.quantity) || 0;

    arr[index22] = {
      ...arr[index22],
      stats: miles2D > 0 ? `${(val22 / miles2D).toFixed(1)} per mi` : '',
    };
  };

  // Helper: “Open Cut Dirt Roads” stats (no 23)
  const computeOpenCutDirtStats = (arr, indexMap) => {
    const index3 = indexMap[3];
    const index23 = indexMap[23];
    if (index3 === undefined || index23 === undefined) return;

    const lengthFeet = Number(arr[index3]?.quantity) || 0;
    const miles2D = lengthFeet / 5280;
    const val23 = Number(arr[index23]?.quantity) || 0;

    arr[index23] = {
      ...arr[index23],
      stats: miles2D > 0 ? `${Math.round(val23 / miles2D)} per mi` : '',
    };
  };

  // Helper: “Length of Class 2 Segment” stats (no 24)
  const computeClass2SegmentStats = (arr, indexMap) => {
    const index3 = indexMap[3];
    const index24 = indexMap[24];
    if (index3 === undefined || index24 === undefined) return;

    const lengthFeet = Number(arr[index3]?.quantity) || 0;
    const miles2D = lengthFeet / 5280;
    const val24 = Number(arr[index24]?.quantity) || 0;

    arr[index24] = {
      ...arr[index24],
      stats: miles2D > 0 ? `${Math.round(val24 / miles2D)} per mi` : '',
    };
  };

  // Helper: “Length of Class 3 Segment” stats (no 25)
  const computeClass3SegmentStats = (arr, indexMap) => {
    const index3 = indexMap[3];
    const index25 = indexMap[25];
    if (index3 === undefined || index25 === undefined) return;

    const lengthFeet = Number(arr[index3]?.quantity) || 0;
    const miles2D = lengthFeet / 5280;
    const val25 = Number(arr[index25]?.quantity) || 0;

    arr[index25] = {
      ...arr[index25],
      stats: miles2D > 0 ? `${Math.round(val25 / miles2D)} per mi` : '',
    };
  };

  // Helper: “Length of Class 4 Segment” stats (no 26)
  const computeClass4SegmentStats = (arr, indexMap) => {
    const index3 = indexMap[3];
    const index26 = indexMap[26];
    if (index3 === undefined || index26 === undefined) return;

    const lengthFeet = Number(arr[index3]?.quantity) || 0;
    const miles2D = lengthFeet / 5280;
    const val26 = Number(arr[index26]?.quantity) || 0;

    arr[index26] = {
      ...arr[index26],
      stats: miles2D > 0 ? `${Math.round(val26 / miles2D)} per mi` : '',
    };
  };

  // Helper: “Foreign Line Crossings” stats (no 27)
  const computeForeignLineStats = (arr, indexMap) => {
    const index3 = indexMap[3];
    const index27 = indexMap[27];
    if (index3 === undefined || index27 === undefined) return;

    const lengthFeet = Number(arr[index3]?.quantity) || 0;
    const miles2D = lengthFeet / 5280;
    const val27 = Number(arr[index27]?.quantity) || 0;

    arr[index27] = {
      ...arr[index27],
      stats: miles2D > 0 ? `${(val27 / miles2D).toFixed(1)} per mi` : '',
    };
  };

  // Helper: “Collocation” stats (no 28) → %
  const computeCollocationStats = (arr, indexMap) => {
    const index3 = indexMap[3];
    const index28 = indexMap[28];
    if (index3 === undefined || index28 === undefined) return;

    const lengthFeet = Number(arr[index3]?.quantity) || 0;
    const miles2D = lengthFeet / 5280;
    const val28 = Number(arr[index28]?.quantity) || 0;

    arr[index28] = {
      ...arr[index28],
      stats: miles2D > 0 ? `${Math.round((val28 / miles2D) * 100)}%` : '',
    };
  };

  // Helper: “Rock Ditch” stats (no 29) → % of route‐feet in rock ditch
  const computeRockDitchStats = (arr, indexMap) => {
    const index3 = indexMap[3];
    const index29 = indexMap[29];
    if (index3 === undefined || index29 === undefined) return;

    const val3 = Number(arr[index3]?.quantity) || 0;
    const val29 = Number(arr[index29]?.quantity) || 0;

    arr[index29] = {
      ...arr[index29],
      stats: val3 > 0 ? `${Math.round((val29 / val3) * 100)}%` : '',
    };
  };

  // Helper: “Open Cut Wetlands & Waterbodies” stats (no 31) → %
  const computeWetlandsStats = (arr, indexMap) => {
    const index3 = indexMap[3];
    const index31 = indexMap[31];
    if (index3 === undefined || index31 === undefined) return;

    const val3 = Number(arr[index3]?.quantity) || 0;
    const val31 = Number(arr[index31]?.quantity) || 0;

    arr[index31] = {
      ...arr[index31],
      stats: val3 > 0 ? `${Math.round((val31 / val3) * 100)}%` : '',
    };
  };

  // Helper: “Flood Plain or Hydric Soil” stats (no 32) → %
  const computeFloodPlainStats = (arr, indexMap) => {
    const index3 = indexMap[3];
    const index32 = indexMap[32];
    if (index3 === undefined || index32 === undefined) return;

    const val3 = Number(arr[index3]?.quantity) || 0;
    const val32 = Number(arr[index32]?.quantity) || 0;

    arr[index32] = {
      ...arr[index32],
      stats: val3 > 0 ? `${Math.round((val32 / val3) * 100)}%` : '',
    };
  };

  // Helper: “Mats” (no 33). This one is a big formula referencing no 19, 13, 15, 17, 27, 31, 32.
  const computeMats = (arr, indexMap) => {
    const index6 = indexMap[6];
    const index13 = indexMap[13];
    const index15 = indexMap[15];
    const index17 = indexMap[17];
    const index19 = indexMap[19];
    const index27 = indexMap[27];
    const index31 = indexMap[31];
    const index32 = indexMap[32];
    const index33 = indexMap[33];

    if (
      index13 === undefined ||
      index15 === undefined ||
      index17 === undefined ||
      index19 === undefined ||
      index27 === undefined ||
      index31 === undefined ||
      index32 === undefined ||
      index33 === undefined ||
      index6 === undefined
    ) {
      return;
    }

    const val6 = Number(arr[index6]?.quantity) || 0;
    const val13 = Number(arr[index13]?.quantity) || 0;
    const val15 = Number(arr[index15]?.quantity) || 0;
    const val17 = Number(arr[index17]?.quantity) || 0;
    const val19 = Number(arr[index19]?.quantity) || 0;
    const val27 = Number(arr[index27]?.quantity) || 0;
    const val31 = Number(arr[index31]?.quantity) || 0;
    const val32 = Number(arr[index32]?.quantity) || 0;

    // mats = 10*no19 + 4*(no13+no15+no17) + 6*no27 + no31/4 + ((0.5*(no32 - no31))/4)
    const matsCalc = 10 * val19 + 4 * (val13 + val15 + val17) + 6 * val27 + val31 / 4 + (0.5 * (val32 - val31)) / 4;

    const routeLength3D = val6 / 5280;
    const roundedUp = Math.ceil(matsCalc / 100) * 100;

    arr[index33] = {
      ...arr[index33],
      quantity: roundedUp,
      stats: roundedUp > 0 ? `${Math.round(roundedUp / routeLength3D)} per mi` : '',
    };
  };

  // Helper: “Segmentable Elbows” stats (no 34) → elbows per route mi (based on 2D length no 3)
  const computeSegmentableElbows = (arr, indexMap) => {
    const index3 = indexMap[3];
    const index34 = indexMap[34];
    if (index3 === undefined || index34 === undefined) return;

    const val3 = Number(arr[index3]?.quantity) || 0;
    const elbows = Number(arr[index34]?.quantity) || 0;
    const routeMiles = val3 / 5280;

    arr[index34] = {
      ...arr[index34],
      stats: routeMiles > 0 ? `${(elbows / routeMiles).toFixed(1)} per mi` : '',
    };
  };

  // Helper: “Mainline Valves” stats (no 35) → pipeMiles per valve (based on 2D length no 3)
  const computeValvesPerMile = (arr, indexMap) => {
    const index3 = indexMap[3];
    const index35 = indexMap[35];
    if (index3 === undefined || index35 === undefined) return;

    const val3 = Number(arr[index3]?.quantity) || 0;
    const valves = Number(arr[index35]?.quantity) || 0;
    const routeMiles = val3 / 5280;

    arr[index35] = {
      ...arr[index35],
      stats: valves > 0 && routeMiles > 0 ? `${Math.round(routeMiles / valves)} mi avg` : '',
    };
  };

  // Helper: Re-compute no 39 (“CP Test Stations”) quantity & stats in one shot:
  const computeCpTestStations = (arr, indexMap) => {
    const index4 = indexMap[4];
    const index7 = indexMap[7];
    const index27 = indexMap[27];
    const index13 = indexMap[13];
    const index19 = indexMap[19];
    const index39 = indexMap[39];
    if (
      index4 === undefined ||
      index7 === undefined ||
      index27 === undefined ||
      index13 === undefined ||
      index19 === undefined ||
      index39 === undefined
    ) {
      return;
    }

    const route2DMiles = Number(arr[index4]?.quantity) || 0;
    const route3DMiles = Number(arr[index7]?.quantity) || 0;
    const val27 = Number(arr[index27]?.quantity) || 0;
    const val13 = Number(arr[index13]?.quantity) || 0;
    const val19 = Number(arr[index19]?.quantity) || 0;

    const testQuantity39 = Math.ceil(route3DMiles) + val27 + (val13 + val19) * 2;
    arr[index39] = {
      ...arr[index39],
      quantity: testQuantity39,
      stats: testQuantity39 > 0 && route2DMiles > 0 ? `${(testQuantity39 / route2DMiles).toFixed(1)} per mi` : '',
    };
  };

  // Helper: “Anode & Rectifier” stats (no 40) → mi segments per Anode
  const computeAnodeStats = (arr, indexMap) => {
    const index4 = indexMap[4];
    const index7 = indexMap[7];
    const index40 = indexMap[40];

    const route2DMiles = Number(arr[index4]?.quantity) || 0;
    const route3DFeet = Number(arr[index7]?.quantity) || 0;
    const route3DMiles = route3DFeet / 5280;
    const numAnodes = Number(arr[index40]?.quantity) || 0;

    arr[index40] = {
      ...arr[index40],
      quantity: Math.max(1, Math.ceil(route2DMiles / 100)),
      stats: `${(route2DMiles / Math.max(1, Math.ceil(route2DMiles / 100))).toFixed(1)} mi segments`,
    };
  };

  const recalcAll = (arr) => {
    // Always recompute route‐length first:
    computeRouteLengthFields(arr, noToIndexMap);
    // Then recompute every dependent statistic in a safe sequence:
    computeAccessRoadStats(arr, noToIndexMap);
    computeTractStats(arr, noToIndexMap);
    computeBoreClass1Stats(arr, noToIndexMap);
    computeBoreClass1LengthStats(arr, noToIndexMap);
    computeBoreClass2Stats(arr, noToIndexMap);
    computeBoreClass2LengthStats(arr, noToIndexMap);
    computeBoreClass3Stats(arr, noToIndexMap);
    computeBoreClass3LengthStats(arr, noToIndexMap);
    computeHddStats(arr, noToIndexMap);
    computeHddLengthStats(arr, noToIndexMap);
    computeOpenCutHAStats(arr, noToIndexMap);
    computeOpenCutDirtStats(arr, noToIndexMap);
    computeClass2SegmentStats(arr, noToIndexMap);
    computeClass3SegmentStats(arr, noToIndexMap);
    computeClass4SegmentStats(arr, noToIndexMap);
    computeForeignLineStats(arr, noToIndexMap);
    computeCollocationStats(arr, noToIndexMap);
    computeRockDitchStats(arr, noToIndexMap);
    computeWetlandsStats(arr, noToIndexMap);
    computeFloodPlainStats(arr, noToIndexMap);
    computeMats(arr, noToIndexMap);
    computeSegmentableElbows(arr, noToIndexMap);
    computeValvesPerMile(arr, noToIndexMap);
    computeCpTestStations(arr, noToIndexMap);
    computeAnodeStats(arr, noToIndexMap);
  };

  // Main cell‐value‐changed handler (rewritten to call our helpers)
  const handleGridEdit = useCallback(
    (params) => {
      const updatedData = [...gridData];
      const changedNo = params.data.no;

      // Any edit to no 3 or no 5 should trigger re‐calc of Route Length fields
      if (changedNo === 3 || changedNo === 5) {
        computeRouteLengthFields(updatedData, noToIndexMap);
      }

      // If no 11 or no 3 → recompute Access Road stats
      if (changedNo === 11 || changedNo === 3) {
        computeAccessRoadStats(updatedData, noToIndexMap);
      }

      // If no 12 or no 3 → Tract stats
      if (changedNo === 12 || changedNo === 3) {
        computeTractStats(updatedData, noToIndexMap);
      }

      // Bore Class 1 (no 13) or route length (no 3) → recompute Class 1 stats
      if (changedNo === 13 || changedNo === 3 || changedNo === 14) {
        computeBoreClass1Stats(updatedData, noToIndexMap);
        computeBoreClass1LengthStats(updatedData, noToIndexMap);
      }

      // Bore Class 2 (no 15) or no 3 → recompute Class 2 stats
      if (changedNo === 16 || changedNo === 15) {
        computeBoreClass2Stats(updatedData, noToIndexMap);
        computeBoreClass2LengthStats(updatedData, noToIndexMap);
      }

      // Bore Class 3 (no 17) or no 3 → recompute Class 3 stats
      if (changedNo === 17 || changedNo === 18) {
        computeBoreClass3Stats(updatedData, noToIndexMap);
        computeBoreClass3LengthStats(updatedData, noToIndexMap);
      }

      // HDDs (no 19) or no 3 → recompute HDD stats
      if (changedNo === 19 || changedNo === 20) {
        computeHddStats(updatedData, noToIndexMap);
        computeHddLengthStats(updatedData, noToIndexMap);
      }

      // Open Cut Heavy Haul (no 22) or no 3
      if (changedNo === 22 || changedNo === 3) {
        computeOpenCutHAStats(updatedData, noToIndexMap);
      }

      // Open Cut Dirt (no 23) or no 3
      if (changedNo === 23 || changedNo === 3) {
        computeOpenCutDirtStats(updatedData, noToIndexMap);
      }

      // Class 2 segment (no 24) or no 3
      if (changedNo === 24 || changedNo === 3) {
        computeClass2SegmentStats(updatedData, noToIndexMap);
      }

      // Class 3 segment (no 25) or no 3
      if (changedNo === 25 || changedNo === 3) {
        computeClass3SegmentStats(updatedData, noToIndexMap);
      }

      // Class 4 segment (no 26) or no 3
      if (changedNo === 26 || changedNo === 3) {
        computeClass4SegmentStats(updatedData, noToIndexMap);
      }

      // Foreign Line (no 27) or no 3
      if (changedNo === 27 || changedNo === 3) {
        computeForeignLineStats(updatedData, noToIndexMap);
      }

      // Collocation (no 28) or no 3
      if (changedNo === 28 || changedNo === 3) {
        computeCollocationStats(updatedData, noToIndexMap);
      }

      // Rock Ditch (no 29) or no 3
      if (changedNo === 29 || changedNo === 3) {
        computeRockDitchStats(updatedData, noToIndexMap);
      }

      // Wetlands (no 31) or no 3
      if (changedNo === 31 || changedNo === 3) {
        computeWetlandsStats(updatedData, noToIndexMap);
      }

      // Flood Plain (no 32) or no 3
      if (changedNo === 32 || changedNo === 3) {
        computeFloodPlainStats(updatedData, noToIndexMap);
      }

      // Mats (no 33) logic depends on no 13,15,17,19,27,31,32 or no 3→33
      if ([3, 13, 15, 17, 19, 27, 31, 32].includes(changedNo)) {
        computeMats(updatedData, noToIndexMap);
      }

      // Elbows (no 34) or no 3
      if (changedNo === 34 || changedNo === 3) {
        computeSegmentableElbows(updatedData, noToIndexMap);
      }

      // Valves (no 35) or no 3
      if (changedNo === 35 || changedNo === 3) {
        computeValvesPerMile(updatedData, noToIndexMap);
      }

      // CP Test Stations (no 39): depends on 7,13,19,27 or no 3
      if ([3, 7, 13, 19, 27].includes(changedNo)) {
        computeCpTestStations(updatedData, noToIndexMap);
      }

      // Anode & Rectifier (no 40): depends on 6 or no 40 itself
      if (changedNo === 3 || changedNo === 5) {
        computeAnodeStats(updatedData, noToIndexMap);
      }

      // Finally, push the entire updatedData set once:
      setGridData(updatedData);
    },
    [gridData, noToIndexMap]
  );

  const handlePasteEnd = () => {
    const updatedData = [...gridData];
    recalcAll(updatedData);
    setGridData(updatedData);
  };

  // Pipe Summary. This recomputes whenever gridData changes.
  const pipeSummaryData = useMemo(() => {
    const index6 = noToIndexMap[6];
    const index14 = noToIndexMap[14];
    const index16 = noToIndexMap[16];
    const index18 = noToIndexMap[18];
    const index20 = noToIndexMap[20];
    const index21 = noToIndexMap[21];
    const index24 = noToIndexMap[24];
    const index25 = noToIndexMap[25];
    const index26 = noToIndexMap[26];

    const val6 = Number(gridData[index6]?.quantity) || 0;
    const val20 = Number(gridData[index20]?.quantity) || 0;
    const val21 = Number(gridData[index21]?.quantity) || 0;

    const routeLengthFeet = val6;
    const class2Footage = (Number(gridData[index24]?.quantity) || 0) - (Number(gridData[index16]?.quantity) || 0);
    const class3Footage = (Number(gridData[index25]?.quantity) || 0) - (Number(gridData[index18]?.quantity) || 0);
    const class4Footage = Number(gridData[index26]?.quantity) || 0;
    const boreClass1 = Number(gridData[index14]?.quantity) || 0;
    const boreClass2 = Number(gridData[index16]?.quantity) || 0;
    const boreClass3 = Number(gridData[index18]?.quantity) || 0;
    const hddDirectPipe = val20 + val21;

    const class1Footage = Math.max(
      routeLengthFeet -
        (class2Footage + class3Footage + class4Footage + boreClass1 + boreClass2 + boreClass3 + hddDirectPipe),
      0
    );

    const summaryRows = [
      { type: 'Class 1', quantity: Math.round(class1Footage).toLocaleString(), units: 'ft' },
      { type: 'Class 2', quantity: class2Footage.toLocaleString(), units: 'ft' },
      { type: 'Class 3', quantity: class3Footage.toLocaleString(), units: 'ft' },
      { type: 'Class 4', quantity: class4Footage.toLocaleString(), units: 'ft' },
      { type: 'Bore Pipe Class 1', quantity: boreClass1.toLocaleString(), units: 'ft' },
      { type: 'Bore Pipe Class 2', quantity: boreClass2.toLocaleString(), units: 'ft' },
      { type: 'Bore Pipe Class 3', quantity: boreClass3.toLocaleString(), units: 'ft' },
      { type: 'HDD/Direct Pipe', quantity: hddDirectPipe.toLocaleString(), units: 'ft' },
    ];

    const totalFootage = Math.max(
      class1Footage +
        class2Footage +
        class3Footage +
        class4Footage +
        boreClass1 +
        boreClass2 +
        boreClass3 +
        hddDirectPipe,
      0
    );

    return [
      ...summaryRows,
      {
        type: 'Total Footage',
        quantity: Math.round(totalFootage).toLocaleString(),
        units: 'ft',
        isTotal: true,
      },
    ];
  }, [gridData, noToIndexMap]);

  // Column definitions (same as your original)
  const columnDefs = useMemo(
    () => [
      {
        headerName: 'No.',
        valueGetter: (params) => params.data.rowNumber ?? params.data.no,
        width: 80,
        cellStyle: { backgroundColor: '#f0f0f0', color: '#999' },
      },
      {
        headerName: 'Category',
        field: 'category',
        width: 110,
        cellStyle: { backgroundColor: '#f0f0f0', color: '#999' },
      },
      { headerName: 'Type', field: 'type', flex: 1, cellStyle: { backgroundColor: '#f0f0f0', color: '#999' } },
      {
        headerName: 'Quantity',
        field: 'quantity',
        width: 150,
        editable: (params) =>
          params.data.no !== 4 &&
          params.data.no !== 6 &&
          params.data.no !== 7 &&
          params.data.no !== 33 &&
          params.data.no !== 39 &&
          params.data.no !== 40,
        valueFormatter: ({ value }) => {
          const selectValues = ['Natural Gas', 'NGL', 'Hydrogen'];

          //  If the value is a string and matches one of the select values, return it as-is
          if (typeof value === 'string' && selectValues.includes(value)) {
            return value;
          }

          // If the value is empty, null, or NaN, return an empty string
          if (value === '' || value === null || isNaN(Number(value))) {
            return '';
          }

          // Otherwise, format the number with locale formatting
          return Number(value).toLocaleString();
        },

        valueParser: ({ newValue }) => {
          // Convert to string, trim whitespace
          let cleaned = newValue?.toString().trim();
          if (!cleaned) return '';

          // Remove commas and percent signs
          cleaned = cleaned.replace(/,/g, '').replace(/%/g, '');

          // Try parsing to a number; if it’s not a valid number, return the raw string
          const asNum = Number(cleaned);
          return isNaN(asNum) ? cleaned : asNum;
        },
        cellEditorSelector: (params) => {
          if (params.data.no === 1) {
            return {
              component: 'agSelectCellEditor',
              params: {
                values: [
                  '2.875',
                  '3.5',
                  '4.5',
                  '6.625',
                  '8.625',
                  '10.75',
                  '12.75',
                  '16',
                  '18',
                  '20',
                  '24',
                  '26',
                  '30',
                  '36',
                  '42',
                  '48',
                ],
              },
            };
          } else if (params.data.no === 2) {
            return {
              component: 'agSelectCellEditor',
              params: {
                values: ['Natural Gas', 'NGL', 'Hydrogen'],
              },
            };
          }
          return null;
        },
        cellStyle: (params) => {
          const nonEditableTypes = [
            'Mats',
            'CP Test Stations',
            'Anode & Rectifier',
            'Route Length 2D',
            'Route Length 3D',
          ];
          return nonEditableTypes.includes(params.data.type)
            ? { backgroundColor: '#f0f0f0', color: '#999' }
            : { backgroundColor: '#ffffff', color: '#000' };
        },
      },
      { headerName: 'Units', field: 'units', width: 100, cellStyle: { backgroundColor: '#f0f0f0', color: '#999' } },
      {
        headerName: 'By',
        field: 'by',
        width: 100,
        editable: true,
        cellStyle: { backgroundColor: '#ffffff', color: '#000' },
      },
      { headerName: 'Notes', field: 'notes', flex: 2, cellStyle: { backgroundColor: '#f0f0f0', color: '#999' } },
      {
        headerName: 'Data Source',
        field: 'dataSource',
        flex: 1,
        cellStyle: { backgroundColor: '#f0f0f0', color: '#999' },
      },
      { headerName: 'Stats', field: 'stats', flex: 1, cellStyle: { backgroundColor: '#f0f0f0', color: '#999' } },
    ],
    []
  );

  const handleNext = async () => {
    setLoading(true);
    try {
      await saveQuantities({
        estimateId: formData.estimateId,
        quantities: gridData,
      });

      setFormData((prev) => ({ ...prev, quantities: gridData }));
      onComplete();
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fade in>
      <Box display="flex" flexDirection="column" gap={2} mt={4}>
        <Paper
          elevation={3}
          sx={{
            p: 3,
            backgroundColor: '#F9F9F9',
            borderRadius: 2,
            borderLeft: '4px solid #223A64',
          }}
        >
          <Typography
            variant="h6"
            mb={2}
            sx={{
              backgroundColor: '#223A64',
              color: 'white',
              padding: '8px 16px',
              borderRadius: 1,
            }}
          >
            Quantities
          </Typography>
          <Box sx={{ width: '100%' }}>
            <AgGridReact
              rowData={gridData}
              columnDefs={columnDefs}
              defaultColDef={{ resizable: true }}
              onCellValueChanged={handleGridEdit}
              onPasteEnd={handlePasteEnd}
              cellSelection={true}
              singleClickEdit={true}
              getRowId={(params) => `row-${params.data?.rowNumber ?? params.rowIndex}`}
              rowSelection={{
                mode: 'multiRow',
                checkboxes: false,
                headerCheckbox: false,
                suppressRowClickSelection: true,
              }}
              domLayout="autoHeight"
            />
          </Box>
        </Paper>

        <Paper
          elevation={3}
          sx={{
            p: 3,
            backgroundColor: '#F9F9F9',
            borderRadius: 2,
            borderLeft: '4px solid #5A7AA1',
          }}
        >
          <Typography
            variant="h6"
            mb={2}
            sx={{
              backgroundColor: '#5A7AA1',
              color: 'white',
              padding: '8px 16px',
              borderRadius: 1,
            }}
          >
            Pipe Quantity Summary
          </Typography>
          <Box sx={{ width: '100%' }}>
            <AgGridReact
              rowData={pipeSummaryData}
              columnDefs={[
                {
                  headerName: 'Line Class',
                  field: 'type',
                  flex: 2,
                  cellStyle: (params) =>
                    params.data.isTotal
                      ? {
                          backgroundColor: '#dbeafe',
                          fontWeight: 'bold',
                          color: '#000',
                          borderTop: '2px solid #1e3a8a',
                        }
                      : {
                          backgroundColor: '#f0f0f0',
                          color: '#999',
                        },
                },
                {
                  headerName: 'Quantity (ft)',
                  field: 'quantity',
                  flex: 1,
                  valueFormatter: (params) => `${params.value} ft`,
                  cellStyle: (params) =>
                    params.data.isTotal
                      ? {
                          backgroundColor: '#dbeafe',
                          fontWeight: 'bold',
                          color: '#000',
                          borderTop: '2px solid #1e3a8a',
                        }
                      : {
                          backgroundColor: '#f0f0f0',
                          color: '#999',
                        },
                },
              ]}
              defaultColDef={{
                resizable: true,
                editable: false,
                suppressHeaderMenuButton: true,
                suppressMovable: true,
              }}
              domLayout="autoHeight"
              suppressMovableColumns
              suppressCellFocus={true}
            />
          </Box>
        </Paper>

        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading}
            startIcon={loading && <CircularProgress size={18} color="inherit" />}
          >
            {loading ? 'Saving...' : 'Next'}
          </Button>
        </Box>
      </Box>
    </Fade>
  );
};

export default Quantities;
